import React from 'react';

import {isDevelopment} from '../../../../libs/shared/core/src/helpers/environment/envHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {DataEventContext} from '../../../../libs/shared/core/src/models/tracking/dataEventContexts';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {trackDataEvent} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasOnboardingPrefilledWorkspaceV3Progress} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {AppParametricSelector, AppSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {SandboxScenariosEnum} from '../../../helpers/sandbox/sandboxScenarioHelpers';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../models/url/models/types';
import {buildChannelPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsHomeDestinationsEnum} from '../../../settings/home/settingsHomeTypes';
import {AppState} from '../../../store/appState';
import {ThunkAction} from '../../../store/appThunk';
import {getShouldAddFirstSharedChannel} from '../../../store/data/channels/channelsSelectors';
import {
  getActivationPreferences,
  getWorkflowsTaskIsComplete,
} from '../../../store/data/company/companySelectors';
import {
  getIsSandboxGeneralInboxTicketingEnabled,
  getSandboxAnalyticsInbox,
  getSandboxChatInbox,
} from '../../../store/data/inboxes/inboxesSelectors';
import {getBasicsGuideIsCompleted} from '../../../store/data/preferences/preferencesSelectors';
import {
  navigateToNewSandboxDemo,
  navigateToSandboxAnalyticsDemoAsyncAction,
  navigateToSandboxChatDemoAsyncAction,
} from '../../../store/data/teammates/teammatesActions';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {getCompanyHasSandbox, getFirstTeamUserIsAdminOf} from '../../../store/data/teams/teamsSelectors';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {
  getIsSandboxAnalyticsDemoComplete,
  getIsSandboxChatDemoComplete,
  getIsSandboxGeneralDemoComplete,
  getShouldAddTeammate,
} from '../../../store/stateless/onboardingSelectors';
import {InboxesTopbarTooltip} from '../../inboxes/topbar/inboxesTopbarTooltip';
import {OnboardingGuidesEnum} from '../guide/onboardingGuideTypes';
import {AdminSetupGuideTaskDefinition, AdminSetupGuideTaskSelectorParams} from './onboardingTypes';

/*
 * Constants.
 */

/** Simulates course completion in development. */
const isLmsCourseTasksCompleteInDevelopment = true;

const adminSetupGuideContext: DataEventContext = Object.freeze({
  name: 'admin_setup_guide',
  payload: {},
});

/*
 * Selectors.
 */

function getIsBasicsGuideTaskComplete(state: AppState, params: AdminSetupGuideTaskSelectorParams) {
  const isCompleted = getBasicsGuideIsCompleted(state);

  // Prior to the Basics Guide we used an LMS course hosted by Northpass to teach customers about inboxes, and channels.
  const inboxesVsChannelsCourseTaskSetupGuideUuid = '3331d5d0-f466-4ef6-b66e-a994de1ff808';

  // If they don't have any progress in teammate preferences check if they've completed the LMS course, so existing
  // customers do not "lose progress."
  if (isCompleted === null) {
    return params.completedCourses?.[inboxesVsChannelsCourseTaskSetupGuideUuid] ?? false;
  }

  return isCompleted;
}

function makeGetIsCourseTaskComplete(
  uuid: string,
): AppParametricSelector<AdminSetupGuideTaskSelectorParams, boolean> {
  return (_state, params) =>
    (isDevelopment() && isLmsCourseTasksCompleteInDevelopment) ||
    (params.completedCourses?.[uuid] ?? params.shouldAssumeCoursesAreComplete ?? false);
}

/*
 * Sandbox basics/core demo.
 */

const sandboxGeneralDemoTask = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'sandboxGeneralDemo',
  getIsTaskVisible: getCompanyHasSandbox,
  getIsTaskComplete: getIsSandboxGeneralDemoComplete,
  computeTrackingObject: () => 'explore_sandbox',
  isUnnumbered: true,
  title: <Trans id="admin-setup-guide-demos-screen-core-capabilities-title">Core capabilities</Trans>,
  description: (
    <Trans id="admin-setup-guide-demos-screen-core-capabilities-description">
      Resolve customer inquiries faster through collaboration and automation.
    </Trans>
  ),
  startTask: ({dispatch, state}) => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_PRODUCT_DEMOS_CORE,
    );
    const isGeneralSandboxInboxTicketing = getIsSandboxGeneralInboxTicketingEnabled(state);

    return dispatch(
      navigateToNewSandboxDemo({
        scenario: isGeneralSandboxInboxTicketing
          ? SandboxScenariosEnum.SCENARIO5_ACT1
          : SandboxScenariosEnum.SCENARIO3_ACT1,
        step: isGeneralSandboxInboxTicketing
          ? 'start-scenario-5-conversation-1'
          : 'start-scenario-3-conversation-1',
      }),
    );
  },
});

/*
 * Sandbox analytics demo.
 */

const sandboxAnalyticsDemoTask = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'sandboxAnalyticsDemo',
  getIsTaskVisible: (state) => {
    const inbox = getSandboxAnalyticsInbox(state);
    return inbox !== undefined;
  },
  getIsTaskComplete: getIsSandboxAnalyticsDemoComplete,
  computeTrackingObject: () => 'explore_sandbox_analytics',
  isUnnumbered: true,
  title: <Trans id="admin-setup-guide-demos-screen-analytics-title">Analytics</Trans>,
  description: (
    <Trans id="admin-setup-guide-demos-screen-analytics-description">
      Explore how analytics surfaces insights to help make business decisions.
    </Trans>
  ),
  startTask: ({dispatch}) => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_PRODUCT_DEMOS_ANALYTICS,
    );
    return dispatch(navigateToSandboxAnalyticsDemoAsyncAction());
  },
});

/*
 * Sandbox chat demo.
 */

const sandboxAiAnswersDemoTask = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'sandboxChatDemo',
  getIsTaskVisible: (state) => {
    const inbox = getSandboxChatInbox(state);
    return inbox !== undefined;
  },
  getIsTaskComplete: getIsSandboxChatDemoComplete,
  computeTrackingObject: () => 'explore_sandbox_chat',
  isUnnumbered: true,
  title: (
    <Trans id="admin-setup-guide-demos-screen-live-chat-ai-answers-title">
      Live chat with AI and knowledge base
    </Trans>
  ),
  description: (
    <Trans id="admin-setup-guide-demos-screen-live-chat-ai-answers-description">
      Improve customer experience with instant AI resolutions, powered by your knowledge base.
    </Trans>
  ),
  startTask: ({dispatch}) => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_PRODUCT_DEMOS_CHAT,
    );
    return dispatch(navigateToSandboxChatDemoAsyncAction());
  },
});

/*
 * Complete Inboxes vs. Channels Basics Guide
 */

const inboxesVsChannelsBasicsTaskSetupGuide = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'inboxes-vs-channels-basics',
  title: (
    <Trans id="admin-setup-guide-setup-screen-inboxes-vs-channels-basics-title">
      Understand Front inboxes vs. channels
    </Trans>
  ),
  description: (
    <Trans id="admin-setup-guide-setup-screen-inboxes-vs-channels-basics-description">
      Knowing the difference will help you structure Front to work best for your team.
    </Trans>
  ),
  isExternal: false,
  getIsTaskComplete: getIsBasicsGuideTaskComplete,
  startTask: ({setOnboardingGuide}) => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'understand_front_inboxes_vs_channels_basics_guide',
      },
    );
    setOnboardingGuide(OnboardingGuidesEnum.BASICS);
  },
  getIsTaskVisible: () => true,
  computeTrackingObject: () => 'understand_front_inboxes_vs_channels_basics_guide',
});

/*
 * Set up shared workspace.
 */

export const getSharedWorkspaceTaskIsComplete: AppSelector<boolean> = (state) => {
  const shouldUseCompanyActivationPreferences = getHasOnboardingPrefilledWorkspaceV3Progress(state);
  if (!shouldUseCompanyActivationPreferences) {
    return true;
  }

  const companyActivationPreferences = getActivationPreferences(state);
  return Boolean(
    companyActivationPreferences.adminOnboardingResources.circleId &&
      companyActivationPreferences.adminOnboardingResources.inboxId,
  );
};

export const sharedWorkspaceTask = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'sharedWorkspace',
  getIsTaskVisible: () => true,
  getIsTaskComplete: getSharedWorkspaceTaskIsComplete,
  computeTrackingObject: () => 'setup_shared_inbox',
  title: <Trans id="admin-setup-guide-setup-screen-shared-inbox-title">Create your first shared inbox</Trans>,
  description: (
    <Trans id="admin-setup-guide-setup-screen-shared-inbox-description">
      A shared inbox helps you organize requests, collaborate on responses, and automate manual processes.
    </Trans>
  ),
  startTask({setOnboardingGuide}) {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'setup_shared_inbox',
      },
    );
    return setOnboardingGuide(OnboardingGuidesEnum.ADMIN_WIZARD);
  },
});

/*
 * Connect channel.
 */

const isConnectChannelTaskComplete: AppSelector<boolean> = (state) => !getShouldAddFirstSharedChannel(state);

export const connectChannelTask = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'connectChannel',
  getIsTaskVisible: () => true,
  getIsTaskComplete: isConnectChannelTaskComplete,
  computeTrackingObject: (isTaskComplete) => (isTaskComplete ? 'view_channels' : 'add_channel'),
  title: (
    <Trans id="admin-setup-guide-setup-screen-import-messages-title">Connect your first shared channel</Trans>
  ),
  description: (
    <Trans id="admin-setup-guide-setup-screen-import-messages-description">
      Manage customer requests across channels like email, chat, and SMS.
    </Trans>
  ),
  details: (
    <InboxesTopbarTooltip
      firstLine={
        <Trans id="admin-setup-guide-setup-screen-import-messages-info-title">
          What to expect after connecting a channel?
        </Trans>
      }
      secondLine={
        <Trans id="admin-setup-guide-setup-screen-import-messages-info-description">
          <ul>
            <li>
              Explore Front worry-free as you can still maintain your current workflows in your existing
              tools.
            </li>
            <li>You can disconnect anytime.</li>
          </ul>
        </Trans>
      }
    />
  ),
  startTask({dispatch}) {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'add_channel',
      },
    );
    return dispatch(startConnectChannelTaskAction());
  },
});

export function startConnectChannelTaskAction(): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    const firstTeamNamespace = getFirstTeamUserIsAdminOf(state)?.namespace;
    const namespace = firstTeamNamespace ?? getUserNamespace(state);

    if (isConnectChannelTaskComplete(state)) {
      dispatch(
        navigateToLinkAction(
          buildChannelPath({
            rootPage: SettingsRootPagesEnum.CHANNELS,
            namespace,
            resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          }),
        ),
      );
    } else {
      dispatch(
        navigateToLinkAction(
          buildChannelPath({
            rootPage: SettingsRootPagesEnum.CHANNELS,
            namespace,
            resourcePage: SettingsResourcePagesEnum.NEW,
          }),
        ),
      );
    }
  };
}

/*
 * Complete "Best Practices" Front Academy course
 */

export const bestPracticesCourseTaskSetupGuideUuid = 'f03e0799-52eb-4a5b-a53d-6f8f8204c07d';

const bestPracticesCourseTaskSetupGuide = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'best-practices',
  title: (
    <Trans id="admin-setup-guide-setup-screen-managing-title">
      Learn the best practices of managing your Front inboxes
    </Trans>
  ),
  description: (
    <Trans id="admin-setup-guide-setup-screen-managing-description">
      Manage your inboxes like a pro with Front’s powerful features, all covered in this course.
    </Trans>
  ),
  isExternal: true,
  getIsTaskComplete: makeGetIsCourseTaskComplete(bestPracticesCourseTaskSetupGuideUuid),
  startTask: () => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'learn_best_practices_of_managing_front_inboxes',
      },
    );
    openInBrowser(
      `https://academy.front.com/app/courses/${encodeURIComponent(bestPracticesCourseTaskSetupGuideUuid)}`,
    );
  },
  getIsTaskVisible: () => true,
  computeTrackingObject: () => 'learn_best_practices_of_managing_front_inboxes',
});

/*
 * Invite teammates.
 */

export const inviteTeammatesTask = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'inviteTeammates',
  getIsTaskVisible: () => true,
  getIsTaskComplete: (state) => !getShouldAddTeammate(state),
  computeTrackingObject: () => 'invite_team',
  title: <Trans id="admin-setup-guide-setup-screen-invite-teammates-title">Invite your team</Trans>,
  description: (
    <Trans id="admin-setup-guide-setup-screen-invite-teammates-description">
      Collaborating with others is the quickest way to discover the magic of Front.
    </Trans>
  ),
  startTask({openSettingsHomeShortcut, setOnboardingGuide, isQuickInviteTeammatesAvailable}) {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'invite_team',
      },
    );

    if (isQuickInviteTeammatesAvailable) {
      return setOnboardingGuide(OnboardingGuidesEnum.QUICK_INVITE);
    }

    return openSettingsHomeShortcut(SettingsHomeDestinationsEnum.ADD_TEAMMATES);
  },
});

/*
 * Set up rules for your inbox.
 */

const setupRulesTask = Object.freeze<AdminSetupGuideTaskDefinition>({
  key: 'setupRules',
  title: (
    <Trans id="admin-setup-guide-setup-screen-setup-rules-title">
      Set up rules for your first shared inbox
    </Trans>
  ),
  description: (
    <Trans id="admin-setup-guide-setup-screen-setup-rules-description">
      Automate, organize, and prioritize your team&apos;s work by setting up rules and save everyone time
    </Trans>
  ),
  isExternal: false,
  getIsTaskComplete: getWorkflowsTaskIsComplete,
  startTask: ({setOnboardingGuide, state}) => {
    // Params are not used, but are required by the selector
    const isSharedWorkspaceTaskComplete = getWorkflowsTaskIsComplete(state);

    if (!isSharedWorkspaceTaskComplete) {
      trackDataEvent(
        adminSetupGuideContext,
        DataEventActionsEnum.CLICK,
        OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
        {
          item: 'setup_shared_inbox',
        },
      );

      return setOnboardingGuide(OnboardingGuidesEnum.ADMIN_WIZARD);
    }

    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_RULES_ITEM,
      {
        item: 'setup_rules_for_inbox',
      },
    );

    return setOnboardingGuide(OnboardingGuidesEnum.WORKFLOWS);
  },
  getIsTaskVisible: () => true,
  computeTrackingObject: () => 'setup_rules_for_inbox',
});

/*
 * Setup Guide Task Definitions.
 */

export const rulesInSetupGuideAdminOnboardingTaskDefinitions = {
  demos: [sandboxGeneralDemoTask, sandboxAnalyticsDemoTask, sandboxAiAnswersDemoTask],
  setup: [
    sharedWorkspaceTask,
    setupRulesTask,
    inboxesVsChannelsBasicsTaskSetupGuide,
    connectChannelTask,
    inviteTeammatesTask,
    bestPracticesCourseTaskSetupGuide,
  ],
};
